import { useState } from "react";
import { useVerifyOrganization, useDismissVerificationRequest, useAllSheltersForVerification, useDeleteShelter, useToggleContacted } from "@/hooks/useOrganizationVerification";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, BadgeCheck, MapPin, Clock, Search, XCircle, Building, X, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { ShelterEditDialog } from "./ShelterEditDialog";
import type { Shelter } from "@/types/shelter";

export const OrganizationVerificationTab = () => {
  const { data: shelters, isLoading: sheltersLoading } = useAllSheltersForVerification(true);
  const verifyOrganization = useVerifyOrganization();
  const dismissRequest = useDismissVerificationRequest();
  const deleteShelter = useDeleteShelter();
  const toggleContacted = useToggleContacted();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyNotContacted, setShowOnlyNotContacted] = useState(false);
  const [editingShelter, setEditingShelter] = useState<Shelter | null>(null);

  // Shelters with pending verification requests
  const pendingRequests = shelters?.filter(s => 
    s.organization_verification_pending && !s.organization_verified
  ) || [];

  // All shelters for manual verification section
  const filteredShelters = shelters?.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesContacted = !showOnlyNotContacted || !s.contacted;
    return matchesSearch && matchesContacted;
  }) || [];

  const handleVerify = async (shelterId: string, shelterName: string) => {
    try {
      await verifyOrganization.mutateAsync({ shelterId, verified: true });
      toast.success(`${shelterName} zostało zweryfikowane jako organizacja`);
    } catch (error: any) {
      toast.error(error.message || "Błąd weryfikacji");
    }
  };

  const handleUnverify = async (shelterId: string, shelterName: string) => {
    try {
      await verifyOrganization.mutateAsync({ shelterId, verified: false });
      toast.success(`Usunięto weryfikację dla ${shelterName}`);
    } catch (error: any) {
      toast.error(error.message || "Błąd usuwania weryfikacji");
    }
  };

  const handleDismissRequest = async (shelterId: string, shelterName: string) => {
    try {
      await dismissRequest.mutateAsync(shelterId);
      toast.success(`Odrzucono prośbę o weryfikację dla ${shelterName}`);
    } catch (error: any) {
      toast.error(error.message || "Błąd odrzucania prośby");
    }
  };

  const handleDeleteShelter = async (shelterId: string, shelterName: string) => {
    try {
      await deleteShelter.mutateAsync(shelterId);
      toast.success(`Schronisko "${shelterName}" zostało usunięte z bazy`);
    } catch (error: any) {
      toast.error(error.message || "Błąd usuwania schroniska");
    }
  };

  return (
    <div className="space-y-8">
      {/* Pending organization verification requests */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-lg">Oczekujące prośby o weryfikację</h2>
          <Badge variant="secondary">
            {pendingRequests.length} oczekujących
          </Badge>
        </div>

        {sheltersLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : pendingRequests.length > 0 ? (
          <div className="space-y-3">
            {pendingRequests.map((shelter) => (
              <div 
                key={shelter.id} 
                className="p-4 rounded-lg border border-primary/20 bg-primary/[0.03] hover:bg-primary/[0.06] transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-medium">{shelter.name}</p>
                      <Badge variant="outline" className="text-primary/70 border-primary/30">
                        <BadgeCheck className="w-3 h-3 mr-1" />
                        Prośba o weryfikację
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {shelter.city}{shelter.region && `, ${shelter.region}`}
                    </p>
                    {shelter.organization_verification_requested_at && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        Zgłoszono: {format(new Date(shelter.organization_verification_requested_at), "d MMM yyyy, HH:mm", { locale: pl })}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleVerify(shelter.id, shelter.name)}
                      disabled={verifyOrganization.isPending}
                    >
                      {verifyOrganization.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <BadgeCheck className="w-4 h-4 mr-1" />
                      )}
                      Zweryfikuj
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDismissRequest(shelter.id, shelter.name)}
                      disabled={dismissRequest.isPending}
                    >
                      {dismissRequest.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                      <span className="hidden sm:inline ml-1">Odrzuć</span>
                    </Button>
                  </div>
                </div>
                
                <div className="mt-3 p-2 bg-background/50 rounded text-xs text-muted-foreground">
                  <strong>Wskazówka:</strong> Skontaktuj się z organizacją przez email lub Facebook, aby zweryfikować tożsamość przed zatwierdzeniem.
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <BadgeCheck className="w-10 h-10 mx-auto mb-3 text-primary" />
              <p className="font-medium">Brak oczekujących próśb</p>
              <p className="text-sm">Żadna organizacja nie czeka na weryfikację</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Manual verification section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-lg">Ręczna weryfikacja organizacji</h2>
          <Badge variant="outline">
            {shelters?.filter(s => s.organization_verified).length || 0} zweryfikowanych
          </Badge>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Szukaj schroniska po nazwie lub mieście..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            size="sm"
            variant={showOnlyNotContacted ? "default" : "outline"}
            onClick={() => setShowOnlyNotContacted(!showOnlyNotContacted)}
          >
            Nie kontaktowano
          </Button>
        </div>

        {sheltersLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : filteredShelters.length > 0 ? (
          <div className="space-y-2">
            {filteredShelters.map((shelter) => (
              <div 
                key={shelter.id} 
                className="flex items-center justify-between p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Building className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setEditingShelter(shelter)}>
                    <p className="font-medium truncate flex items-center gap-2 hover:text-primary transition-colors">
                      {shelter.name}
                      {shelter.organization_verified && (
                        <BadgeCheck className="w-4 h-4 text-primary flex-shrink-0" />
                      )}
                      {shelter.organization_verification_pending && !shelter.organization_verified && (
                        <Badge variant="outline" className="text-xs">Oczekuje</Badge>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground truncate flex items-center gap-2">
                      {shelter.city}{shelter.region && `, ${shelter.region}`}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleContacted.mutate({ shelterId: shelter.id, contacted: !shelter.contacted }); }}
                        disabled={toggleContacted.isPending}
                        className="cursor-pointer"
                      >
                        {shelter.contacted ? (
                          <Badge variant="outline" className="text-xs text-green-700/70 border-green-600/40 hover:bg-green-50">Skontaktowano</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground hover:bg-muted">Nie kontaktowano</Badge>
                        )}
                      </button>
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingShelter(shelter)}
                    title="Edytuj schronisko"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {shelter.organization_verified ? (
                    <>
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        {shelter.organization_verified_at && 
                          format(new Date(shelter.organization_verified_at), "d MMM yyyy", { locale: pl })
                        }
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnverify(shelter.id, shelter.name)}
                        disabled={verifyOrganization.isPending}
                        className="text-destructive hover:text-destructive"
                      >
                        {verifyOrganization.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <XCircle className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline ml-1">Usuń</span>
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleVerify(shelter.id, shelter.name)}
                      disabled={verifyOrganization.isPending}
                    >
                      {verifyOrganization.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <BadgeCheck className="w-4 h-4 mr-1" />
                      )}
                      Zweryfikuj
                    </Button>
                  )}

                  {/* Delete shelter button with confirmation */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        disabled={deleteShelter.isPending}
                      >
                        {deleteShelter.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Usunąć schronisko?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Czy na pewno chcesz usunąć <strong>"{shelter.name}"</strong> z bazy danych?
                          <br /><br />
                          Ta operacja jest nieodwracalna. Zostaną usunięte także wszystkie powiązane zgłoszenia i historia zmian.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Anuluj</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteShelter(shelter.id, shelter.name)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Usuń schronisko
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <Search className="w-10 h-10 mx-auto mb-3" />
              <p className="font-medium">Nie znaleziono schronisk</p>
              <p className="text-sm">Spróbuj zmienić kryteria wyszukiwania</p>
            </CardContent>
          </Card>
        )}
      </div>
      <ShelterEditDialog
        shelter={editingShelter}
        open={!!editingShelter}
        onOpenChange={(open) => { if (!open) setEditingShelter(null); }}
      />
    </div>
  );
};